// Frontend Sentry wiring.
//
// Reads VITE_SENTRY_DSN at build time. When unset (local dev or tests), all
// exported helpers become harmless no-ops.

import * as Sentry from '@sentry/react'

const DSN = import.meta.env.VITE_SENTRY_DSN
const ENV = import.meta.env.MODE
const RELEASE = import.meta.env.VITE_RELEASE || undefined

let initialized = false

export function initSentry() {
  if (!DSN || initialized) return
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    integrations: [
      Sentry.browserTracingIntegration({ instrumentNavigation: true }),
      // Replay only fires on sessions that produced an error. Session sampling
      // stays at 0 so we don't burn replay quota on healthy traffic.
      Sentry.replayIntegration({
        // Mask PII / form input by default. Opt-in to unmask specific elements
        // later if we need richer reproductions.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],
    // 10% performance traces. Pages, navigations, and fetch spans included.
    // Free tier is 10k transactions/month — adjust if traffic grows.
    tracesSampleRate: 0.1,
    // Don't replay random sessions, but capture full replay when an error fires.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  })
  initialized = true
}

export function setSentryUser(user) {
  if (!DSN) return
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({ id: user.id, email: user.email, username: user.name })
}

// Capture a free-form user feedback message, optionally tied to a Sentry
// event ID (e.g. the eventId from an ErrorBoundary fallback).
//
// Returns the feedback event ID (string) or null if Sentry isn't initialized.
export function sendFeedback({ message, name, email, associatedEventId }) {
  if (!DSN || !message) return null
  return Sentry.captureFeedback({
    message,
    name,
    email,
    associatedEventId,
  })
}

export const ErrorBoundary = Sentry.ErrorBoundary

const reportedErrors = new WeakSet()
export function captureClientError(error, source = 'router') {
  if (!DSN || !error) return
  if (typeof error === 'object') {
    if (reportedErrors.has(error)) return
    reportedErrors.add(error)
  }
  Sentry.captureException(error, { tags: { source } })
}
